<template>
  <div class="landing-page">
    <!-- Navigation -->
    <nav class="landing-nav">
      <div class="landing-nav__container">
        <div class="landing-nav__logo">
          <img 
            :src="logoUrl" 
            alt="ThunderLabs" 
            class="landing-nav__logo-img"
          />
        </div>
        
        <div class="landing-nav__links" :class="{ 'is-open': mobileMenuOpen }">
          <a href="#features" class="landing-nav__link">Características</a>
          <a href="#pricing" class="landing-nav__link">Precios</a>
          <a href="#faq" class="landing-nav__link">FAQ</a>
        </div>
        
        <div class="landing-nav__actions">
          <a href="https://mapp.thunderlabs.cl/users/sign_in" class="landing-nav__login-link">
            <Button 
              label="Ingresar" 
              class="p-button-outlined p-button-secondary"
            />
          </a>
          <Button 
            label="Comenzar Gratis" 
            icon="pi pi-arrow-right"
            iconPos="right"
            class="p-button-action"
            @click="showSignupModal = true"
          />
        </div>
        
        <button 
          class="landing-nav__mobile-toggle"
          @click="mobileMenuOpen = !mobileMenuOpen"
          aria-label="Toggle menu"
        >
          <i :class="mobileMenuOpen ? 'pi pi-times' : 'pi pi-bars'"></i>
        </button>
      </div>
    </nav>
    
    <!-- Main Content -->
    <main ref="mainContent">
      <!-- Sticky Parallax Container: Hero + ValidationDemo -->
      <div class="parallax-container" ref="parallaxContainer">
        <div class="parallax-hero" ref="parallaxHero">
          <HeroSection @cta-click="showSignupModal = true" />
        </div>
        <div class="parallax-overlay">
          <ValidationDemo class="animate-on-scroll" />
        </div>
      </div>
      
      <!-- Trust Logos Bar -->
      <TrustLogos class="animate-on-scroll" />
      
      <!-- Features Section - Apple Scroll Style -->
      <FeaturesAppleScroll />
      
      <!-- Benefits Section -->
      <BenefitsSection />
      
      <!-- How It Works Section -->
      <HowItWorks class="animate-on-scroll" @cta-click="showSignupModal = true" />
      
      <!-- Testimonials Section (hidden for now) -->
      <!-- <TestimonialsSection class="animate-on-scroll" /> -->
      
      <!-- Pricing Section (hidden for now) -->
      <!-- <PricingSection class="animate-on-scroll" /> -->
      
      <!-- FAQ Section -->
      <FaqSection class="animate-on-scroll" />
      
      <!-- Final CTA Banner -->
      <CtaBanner class="animate-on-scroll" @cta-click="showSignupModal = true" />
      
      <!-- Footer -->
      <FooterSection />
    </main>

    <!-- Signup Form Modal -->
    <SignupFormModal 
      v-model:visible="showSignupModal" 
      intro="Comienza tu prueba gratuita hoy. Sin tarjeta de crédito requerida."
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import Button from 'primevue/button'
import HeroSection from './HeroSection.vue'
import ValidationDemo from './ValidationDemo.vue'
import TrustLogos from './TrustLogos.vue'
import FeaturesSection from './FeaturesSection.vue'
import FeaturesAppleScroll from './features/FeaturesAppleScroll.vue'
import BenefitsSection from './BenefitsSection.vue'
import HowItWorks from './HowItWorks.vue'
import TestimonialsSection from './TestimonialsSection.vue'
import PricingSection from './PricingSection.vue'
import FaqSection from './FaqSection.vue'
import CtaBanner from './CtaBanner.vue'
import FooterSection from './FooterSection.vue'
import SignupFormModal from './SignupFormModal.vue'

const mobileMenuOpen = ref(false)
const mainContent = ref(null)
const parallaxContainer = ref(null)
const parallaxHero = ref(null)
const showSignupModal = ref(false)

// Use string to avoid Rollup trying to resolve the path
const logoUrl = '/images/thunder_labs_logo.svg';

// Intersection Observer for scroll animations
let observer = null;

// Scroll handler for parallax fade effect
const handleParallaxScroll = () => {
  if (!parallaxContainer.value || !parallaxHero.value) return;
  
  const container = parallaxContainer.value;
  const hero = parallaxHero.value;
  const rect = container.getBoundingClientRect();
  
  // Calculate how much of the hero has been scrolled past
  // rect.top starts positive, goes negative as we scroll down
  const scrollProgress = Math.max(0, Math.min(1, -rect.top / (window.innerHeight * 0.8)));
  
  // Apply opacity fade (1 -> 0)
  const opacity = 1 - scrollProgress;
  hero.style.opacity = opacity;
};

onMounted(() => {
  // Create intersection observer for scroll-triggered animations
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          // Optional: unobserve after animation to improve performance
          // observer.unobserve(entry.target);
        }
      });
    },
    {
      root: null,
      rootMargin: '0px 0px -100px 0px', // Trigger 100px before element enters viewport
      threshold: 0.1
    }
  );

  // Observe all elements with animate-on-scroll class
  const animatedElements = document.querySelectorAll('.animate-on-scroll');
  animatedElements.forEach((el) => observer.observe(el));
  
  // Add scroll listener for parallax fade
  window.addEventListener('scroll', handleParallaxScroll, { passive: true });
  handleParallaxScroll(); // Initial call
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
  window.removeEventListener('scroll', handleParallaxScroll);
});
</script>

<style lang="scss" scoped>
@import '@/design-system/variables';

// ===========================================
// LANDING PAGE LAYOUT
// ===========================================

.landing-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

// ===========================================
// NAVIGATION
// ===========================================

.landing-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: $tl-z-header;
  background: rgba($tl-white, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba($tl-border-color, 0.5);
  transition: $tl-transition-default;
  
  &.is-scrolled {
    background: $tl-white;
    box-shadow: $tl-shadow-sm;
  }
}

.landing-nav__container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 $tl-page-padding;
  height: $tl-header-height;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 0 $tl-space-4;
  }
}

.landing-nav__logo {
  display: flex;
  align-items: center;
  gap: $tl-space-2;
  text-decoration: none;
}

.landing-nav__logo-img {
  height: 45px;
  width: auto;
}

.landing-nav__logo-text {
  font-size: $tl-font-size-xl;
  font-weight: $tl-font-weight-bold;
  color: $tl-jet;
  
  @media (max-width: 480px) {
    display: none;
  }
}

.landing-nav__links {
  display: flex;
  align-items: center;
  gap: $tl-space-6;
  
  @media (max-width: 1024px) {
    position: fixed;
    top: $tl-header-height;
    left: 0;
    right: 0;
    background: $tl-white;
    flex-direction: column;
    padding: $tl-space-4;
    gap: $tl-space-4;
    border-bottom: 1px solid $tl-border-color-light;
    transform: translateY(-100%);
    opacity: 0;
    pointer-events: none;
    transition: $tl-transition-default;
    
    &.is-open {
      transform: translateY(0);
      opacity: 1;
      pointer-events: all;
    }
  }
}

.landing-nav__link {
  font-size: $tl-font-size-sm;
  font-weight: $tl-font-weight-medium;
  color: $tl-gray-700;
  text-decoration: none;
  transition: color $tl-duration-fast;
  
  &:hover {
    color: $tl-primary;
  }
  
  @media (max-width: 1024px) {
    font-size: $tl-font-size-base;
    width: 100%;
    padding: $tl-space-2;
    text-align: center;
  }
}

.landing-nav__actions {
  display: flex;
  align-items: center;
  gap: $tl-space-3;
  
  @media (max-width: 768px) {
    display: none;
  }
}

.landing-nav__login-link {
  text-decoration: none;
}

// Nav buttons use design system classes (p-button-action, p-button-outlined)

.landing-nav__mobile-toggle {
  display: none;
  background: none;
  border: none;
  padding: $tl-space-2;
  font-size: $tl-font-size-xl;
  color: $tl-jet;
  cursor: pointer;
  
  @media (max-width: 1024px) {
    display: block;
  }
}

// ===========================================
// MAIN CONTENT
// ===========================================

main {
  flex: 1;
  padding-top: $tl-header-height;
}

// ===========================================
// SCROLL ANIMATIONS (Apple-style)
// ===========================================

// Base state for animated sections
:deep(.animate-on-scroll) {
  opacity: 0;
  transform: translateY(40px);
  transition: 
    opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  
  // Visible state when scrolled into view
  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }
}

// Stagger child elements within sections
:deep(.animate-on-scroll.is-visible) {
  // Animate cards and content blocks with stagger
  .features-section__card,
  .benefits-section__card,
  .validation-demo__results,
  .cta-banner__container {
    animation: fadeSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    opacity: 0;
  }
  
  // Stagger delays for multiple items
  .features-section__card:nth-child(1),
  .benefits-section__card:nth-child(1) { animation-delay: 0.1s; }
  .features-section__card:nth-child(2),
  .benefits-section__card:nth-child(2) { animation-delay: 0.2s; }
  .features-section__card:nth-child(3),
  .benefits-section__card:nth-child(3) { animation-delay: 0.3s; }
  .features-section__card:nth-child(4),
  .benefits-section__card:nth-child(4) { animation-delay: 0.4s; }
}

@keyframes fadeSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// ===========================================
// STICKY PARALLAX (Hero to ValidationDemo)
// ===========================================

.parallax-container {
  position: relative;
}

.parallax-hero {
  position: sticky;
  top: 0;
  z-index: 1;
  
  // Hero content stays fixed while scrolling
  :deep(.hero-section) {
    min-height: 100vh;
  }
}

.parallax-overlay {
  position: relative;
  z-index: 2;
  
  // Add subtle shadow at top for depth
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.1),
      transparent
    );
    z-index: 10;
    pointer-events: none;
  }
  
  // Ensure section has solid background to cover hero
  :deep(.validation-demo) {
    background: $tl-white;
    position: relative;
  }
}

// Reduce motion for accessibility
@media (prefers-reduced-motion: reduce) {
  :deep(.animate-on-scroll) {
    opacity: 1;
    transform: none;
    transition: none;
    
    &.is-visible * {
      animation: none !important;
      opacity: 1 !important;
      transform: none !important;
    }
  }
}
</style>
